import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { Button } from '../../components/Button.jsx';
import { hexToRgb, rgbToLab } from '../../lib/colour.js';

const num = (v, def = 0) => Number.isFinite(parseFloat(v)) ? parseFloat(v) : def;

export const NewInkModal = ({ open, onClose, onCreated, bookId, bookName, prefilledName = '' }) => {
  const [name, setName] = useState(prefilledName);
  const [hex, setHex] = useState('');
  const [l, setL] = useState('');
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [pantoneCode, setPantoneCode] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill when opened, including any prefilled name (from registry click)
  useEffect(() => {
    if (open) {
      setName(prefilledName);
      setHex('');
      setL(''); setA(''); setB('');
      setPantoneCode('');
      setNotes('');
      setError(null);

      // Auto-fill Pantone code from name like "Pantone 7427 C"
      const m = prefilledName.match(/Pantone\s+(.+?)\s+([CU])$/i);
      if (m) setPantoneCode(`${m[1]} ${m[2].toUpperCase()}`);
    }
  }, [open, prefilledName]);

  // Hex changes derive LAB (optional helper for the user)
  const deriveLabFromHex = () => {
    const rgb = hexToRgb(hex);
    if (!rgb) return;
    const [L, A, B] = rgbToLab(...rgb);
    setL(L.toFixed(1));
    setA(A.toFixed(1));
    setB(B.toFixed(1));
  };

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const hasLab = l !== '' && a !== '' && b !== '';
      const ink = await api.post(`/ink-books/${bookId}/inks`, {
        name: name.trim(),
        targetLab: hasLab ? { l: num(l), a: num(a), b: num(b) } : null,
        targetHex: hex.trim() || null,
        pantoneCode: pantoneCode.trim() || null,
        notes: notes.trim() || null,
      });
      onCreated?.(ink);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: '#FF8A4C' }}>Add ink to {bookName}</p>
          <p class="m-0 text-sm text-text-secondary">
            Target LAB &amp; hex are optional — leave blank if you'll measure later.
          </p>
        </div>

        <div class="space-y-4 p-5">
          <div>
            <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">Name</p>
            <input
              type="text"
              autoFocus
              value={name}
              onInput={(e) => setName(e.currentTarget.value)}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              placeholder="e.g. Pantone 7427 C"
            />
          </div>

          <div>
            <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
              Target hex <span class="font-medium text-text-muted">· optional</span>
            </p>
            <div class="flex gap-2">
              <input
                type="text"
                value={hex}
                onInput={(e) => setHex(e.currentTarget.value)}
                onBlur={deriveLabFromHex}
                placeholder="#RRGGBB"
                class="flex-1 rounded-md border border-border-strong bg-bg-card px-3 py-2 font-mono text-sm font-medium uppercase text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
              <span
                class="block h-9 w-9 shrink-0 rounded-md border border-border-strong"
                style={hex ? { background: hex } : { background: '#221E1A' }}
              />
            </div>
            <p class="mt-1 text-[11px] font-medium text-text-muted">Tab out of the field to auto-derive LAB.</p>
          </div>

          <div>
            <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
              Target LAB <span class="font-medium text-text-muted">· optional</span>
            </p>
            <div class="grid grid-cols-3 gap-2">
              {[
                { label: 'L', state: l, set: setL },
                { label: 'a', state: a, set: setA },
                { label: 'b', state: b, set: setB },
              ].map(({ label, state, set }) => (
                <div key={label}>
                  <label class="mb-1 block text-[11px] font-semibold text-text-muted">{label}</label>
                  <input
                    type="number"
                    step="0.1"
                    value={state}
                    onInput={(e) => set(e.currentTarget.value)}
                    class="block w-full rounded-md border border-border-strong bg-bg-card px-2 py-1.5 text-right font-serif text-[15px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
              Pantone code <span class="font-medium text-text-muted">· optional</span>
            </p>
            <input
              type="text"
              value={pantoneCode}
              onInput={(e) => setPantoneCode(e.currentTarget.value)}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          <div>
            <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
              Notes <span class="font-medium text-text-muted">· optional</span>
            </p>
            <textarea
              rows={2}
              value={notes}
              onInput={(e) => setNotes(e.currentTarget.value)}
              class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed to create')}
            </p>
          )}
        </div>

        <div class="flex justify-end gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy || !name.trim()}>
            {busy ? 'Adding…' : 'Add ink'}
          </Button>
        </div>
      </div>
    </div>
  );
};
